import axios from "axios";
import qs from "qs";

import API_BASE_URL from "./baseUrl";

const BASE_URL = API_BASE_URL;

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
});

/* -------------------- helpers -------------------- */
const s = (v) => (v == null ? "" : String(v)).trim();
const n = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};
const isFiniteLatLng = (lat, lng) => {
  const la = Number(lat);
  const ln = Number(lng);
  return (
    Number.isFinite(la) &&
    Number.isFinite(ln) &&
    Math.abs(la) <= 90 &&
    Math.abs(ln) <= 180
  );
};
const isMeaningfulCoordinateString = (value) => {
  const raw = String(value || "").trim();
  if (!raw || !raw.includes(",")) return false;
  const parts = raw.split(",");
  if (parts.length !== 2) return false;
  const lat = Number(parts[0].trim());
  const lng = Number(parts[1].trim());
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return false;
  return true;
};
const normalizeCategoryList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => s(item)).filter(Boolean);
  }
  if (!value) return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};
const normalizeLegs = (legs) =>
  (Array.isArray(legs) ? legs : []).map((item) => s(item)).filter(Boolean);
const normalizeStopCoordinatesForDestinations = (coords, destinations) => {
  if (!Array.isArray(coords) || !coords.length) return [];
  return coords
    .map((item) => String(item || "").trim())
    .slice(0, destinations.length)
    .map((item) => (isMeaningfulCoordinateString(item) ? item : ""));
};

const SEARCH_RESULTS_CONFIG_TTL_MS = 60 * 1000;
let searchResultsConfigCache = null;
let searchResultsConfigInflight = null;

export function buildScheduleSearchDebugSnapshot(payload) {
  const p = payload || {};

  const pickupLocation = s(p.pickupResolved || p.pickupLocation);
  const destination = s(p.finalResolved || p.finalDestination || p.destination || "");
  const destinations = normalizeLegs(
    Array.isArray(p.stopsResolved) && p.stopsResolved.length ? p.stopsResolved : p.destinations
  );
  const stopCoordinates = normalizeStopCoordinatesForDestinations(
    Array.isArray(p.stopCoordinates) ? p.stopCoordinates : [],
    destinations
  );
  const pickupDate = p.pickupDate || null;
  const pickupTime = p.pickupTime24 || p.pickupTime || null;
  const dropDate = p.releaseDate || p.dropDate || null;
  const dropTime = p.releaseTime24 || p.dropTime || p.releaseTime || null;
  const categoryList = normalizeCategoryList(
    Array.isArray(p.categories) ? p.categories : p.categories || p.category
  );
  const category =
    categoryList.length > 1 ? "all" : s(p.category) || categoryList[0] || "all";
  const carTypes = Array.isArray(p.carTypes)
    ? p.carTypes.map((x) => s(x)).filter(Boolean)
    : [s(p.carType || p.type || "")].filter(Boolean);
  const travelDistance =
    typeof p.totalDistance === "number" ? p.totalDistance : Number(p.totalDistance);
  const durationHours =
    typeof p.durationHours === "number" ? p.durationHours : Number(p.durationHours);
  const pickupLat = n(p.pickupLat);
  const pickupLng = n(p.pickupLng);
  const dropLat = n(p.dropLat);
  const dropLng = n(p.dropLng);

  const queryParams = {
    pickupLocation,
    pickupLat: isFiniteLatLng(pickupLat, pickupLng) ? pickupLat : undefined,
    pickupLng: isFiniteLatLng(pickupLat, pickupLng) ? pickupLng : undefined,
    dropLat: isFiniteLatLng(dropLat, dropLng) ? dropLat : undefined,
    dropLng: isFiniteLatLng(dropLat, dropLng) ? dropLng : undefined,
    pickupDate,
    pickupTime,
    destinations,
    stopCoordinates: stopCoordinates.length ? stopCoordinates : undefined,
    destination: destination || null,
    dropDate,
    dropTime,
    radius: p.radius ?? 20,
    travelDistance,
    durationHours,
    types: carTypes.length ? carTypes : undefined,
    category: category || "normal",
    roundTrip: !!p.roundTrip,
  };
  const queryString = qs.stringify(queryParams, {
    arrayFormat: "repeat",
    skipNulls: true,
  });

  return {
    normalizedPayload: {
      ...p,
      pickupLocation,
      pickupDate,
      pickupTime24: pickupTime,
      releaseDate: dropDate,
      releaseTime24: dropTime,
      destinations,
      stopCoordinates,
      finalDestination: destination,
      totalDistance: travelDistance,
      durationHours,
      carTypes,
      category,
    },
    queryParams,
    queryString,
  };
}

export function isAbortError(error) {
  return (
    error?.name === "AbortError" ||
    error?.name === "CanceledError" ||
    error?.code === "ERR_CANCELED" ||
    String(error?.message || "").toLowerCase().includes("aborted") ||
    String(error?.message || "").toLowerCase().includes("canceled") ||
    String(error?.message || "").toLowerCase().includes("cancelled")
  );
}

function normalizeLocationSuggestion(item) {
  if (!item) return null;
  if (typeof item === "string") {
    const text = item.trim();
    return text
      ? {
          placeId: "",
          primaryText: text,
          secondaryText: "",
          fullText: text,
          latitude: null,
          longitude: null,
          source: "",
        }
      : null;
  }

  const placeId = item.placeId ?? item.place_id ?? "";
  const primaryText = item.primaryText ?? item.mainText ?? item.structured_formatting?.main_text ?? item.displayName ?? item.name ?? "";
  const secondaryText = item.secondaryText ?? item.secondary_text ?? item.structured_formatting?.secondary_text ?? item.formattedAddress ?? "";
  const fullText = item.fullText ?? item.description ?? [primaryText, secondaryText].filter(Boolean).join(", ");
  const latitude = n(item.latitude ?? item.lat);
  const longitude = n(item.longitude ?? item.lng);
  const source = item.source ?? "";

  const text = s(fullText);
  if (!text) return null;

  return {
    placeId: s(placeId),
    primaryText: s(primaryText) || text,
    secondaryText: s(secondaryText),
    fullText: text,
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
    source: s(source),
  };
}

function normalizeLocationSuggestionArray(data) {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.predictions)
    ? data.predictions
    : Array.isArray(data?.suggestions)
    ? data.suggestions
    : [];

  return raw.map(normalizeLocationSuggestion).filter(Boolean);
}

/** Normalize trip-estimate response so UI can rely on keys */
function normalizeTripEstimateResponse(data) {
  const d = data || {};

  const forwardDistanceKm =
    n(d.forwardDistanceKm) ??
    n(d.forward_distance_km) ??
    n(d.distanceKm) ??
    n(d.totalDistanceKm) ??
    null;

  const durationHours =
    n(d.durationHours) ?? n(d.duration_hours) ?? n(d.hours) ?? null;

  const minDropOffsetMin =
    n(d.minDropOffsetMin) ??
    n(d.min_drop_offset_min) ??
    n(d.minDropMinutes) ??
    null;

  let pickupLat = n(d.pickupLat);
  let pickupLng = n(d.pickupLng);
  let dropLat = n(d.dropLat);
  let dropLng = n(d.dropLng);

  if (
    (pickupLat == null || pickupLng == null) &&
    d.pickupCoords &&
    typeof d.pickupCoords === "object"
  ) {
    pickupLat = n(d.pickupCoords.lat);
    pickupLng = n(d.pickupCoords.lng);
  }
  if (
    (dropLat == null || dropLng == null) &&
    d.dropCoords &&
    typeof d.dropCoords === "object"
  ) {
    dropLat = n(d.dropCoords.lat);
    dropLng = n(d.dropCoords.lng);
  }

  if (
    (pickupLat == null || pickupLng == null) &&
    d.pickup &&
    typeof d.pickup === "object"
  ) {
    pickupLat = n(d.pickup.lat);
    pickupLng = n(d.pickup.lng);
  }
  if (
    (dropLat == null || dropLng == null) &&
    d.drop &&
    typeof d.drop === "object"
  ) {
    dropLat = n(d.drop.lat);
    dropLng = n(d.drop.lng);
  }

  if (!isFiniteLatLng(pickupLat, pickupLng)) {
    pickupLat = null;
    pickupLng = null;
  }
  if (!isFiniteLatLng(dropLat, dropLng)) {
    dropLat = null;
    dropLng = null;
  }

  return {
    ...d,
    forwardDistanceKm,
    durationHours,
    minDropOffsetMin,
    pickupLat,
    pickupLng,
    dropLat,
    dropLng,
  };
}

/* ---------- Autocomplete (public shared flow) ---------- */
export async function autocompleteLocations(input, opts = {}) {
  const q = s(input);
  if (!q) return [];

  const config = {
    params: {},
  };

  if (opts.lat != null && Number.isFinite(Number(opts.lat))) {
    config.params.lat = Number(opts.lat);
  }
  if (opts.lng != null && Number.isFinite(Number(opts.lng))) {
    config.params.lng = Number(opts.lng);
  }
  if (opts.sessionToken) {
    config.params.sessionToken = s(opts.sessionToken);
  }
  if (opts.signal) {
    config.signal = opts.signal;
  }

  try {
    const { data } = await API.get(
      `/api/vehicles/autocomplete/location-predictions/${encodeURIComponent(q)}`,
      config
    );
    return normalizeLocationSuggestionArray(data);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.warn("autocompleteLocations error", error);
    return [];
  }
}

/* ---------- Leg distance (public) ---------- */
export async function fetchLegDistance(origin, destination) {
  const o = s(origin);
  const d = s(destination);
  if (!o || !d) return 0;

  const { data } = await API.get("/api/vehicles/distance", {
    params: { origin: o, destination: d },
  });

  return data && typeof data.totalDistance === "number" ? data.totalDistance : 0;
}

/* ---------- Toll (public) ---------- */
export async function fetchToll({
  pickupLocation,
  destinations = [],
  finalDestination,
}) {
  if (!s(pickupLocation) || !s(finalDestination)) return 0;

  const { data } = await API.post("/api/calculate-toll", {
    pickupLocation,
    destinations,
    finalDestination,
  });

  return Number(data?.toll ?? 0);
}

/* ---------- Optional: resolve lat/lng (public) ---------- */
export async function resolveLatLng(location) {
  const loc = s(location);
  if (!loc) return null;
  const { data } = await API.get("/api/vehicles/resolve-latlng", {
    params: { location: loc },
  });
  const lat = n(data?.lat ?? data?.latitude);
  const lng = n(data?.lng ?? data?.longitude);
  return isFiniteLatLng(lat, lng) ? { lat, lng } : null;
}

export async function getSearchResultsConfig() {
  const now = Date.now();
  if (
    searchResultsConfigCache &&
    now - searchResultsConfigCache.ts <= SEARCH_RESULTS_CONFIG_TTL_MS
  ) {
    console.info("[schedule-config][web] using cached search config", {
      ageMs: now - searchResultsConfigCache.ts,
      scheduleRequestThresholdMinutes:
        searchResultsConfigCache.value?.scheduleRequestThresholdMinutes ?? null,
      mode: searchResultsConfigCache.value?.mode ?? null,
    });
    return searchResultsConfigCache.value;
  }
  if (searchResultsConfigInflight) {
    console.info("[schedule-config][web] awaiting inflight search config request");
    return searchResultsConfigInflight;
  }

  const request = (async () => {
    try {
      const { data } = await API.get("/api/public/app-config/search-mode");
      const value = {
        mode:
          String(data?.mode || "").trim().toUpperCase() === "JSON_CATALOG"
            ? "JSON_CATALOG"
            : "DATABASE",
        scheduleRequestThresholdMinutes:
          Number(data?.scheduleRequestThresholdMinutes) || 0,
        scheduleBookingDiscountPercent:
          Number(data?.scheduleBookingDiscountPercent) || 0,
        scheduleContactDetailsVisibleBeforePickupHours:
          Number(data?.scheduleContactDetailsVisibleBeforePickupHours) || 24,
      };
      console.info("[schedule-config][web] fetched search config", {
        rawScheduleRequestThresholdMinutes: data?.scheduleRequestThresholdMinutes,
        normalizedScheduleRequestThresholdMinutes:
          value.scheduleRequestThresholdMinutes,
        mode: value.mode,
      });
      searchResultsConfigCache = { ts: Date.now(), value };
      return value;
    } catch (error) {
      if (isAbortError(error)) throw error;
      console.warn("getSearchResultsConfig error", error);
      const fallback = {
        mode: "DATABASE",
        scheduleRequestThresholdMinutes: 30,
        scheduleBookingDiscountPercent: 0,
        scheduleContactDetailsVisibleBeforePickupHours: 24,
      };
      console.warn("[schedule-config][web] falling back to default search config", {
        scheduleRequestThresholdMinutes: fallback.scheduleRequestThresholdMinutes,
      });
      searchResultsConfigCache = { ts: Date.now(), value: fallback };
      return fallback;
    } finally {
      searchResultsConfigInflight = null;
    }
  })();

  searchResultsConfigInflight = request;
  return request;
}

/* ---------- Trip estimate (public) ---------- */
const tripEstimateInflight = new Map();
const tripEstimateResultCache = new Map();
const TRIP_ESTIMATE_CACHE_TTL_MS = 30 * 1000;

export async function estimateTrip(payload) {
  const p = payload || {};

  const roundTrip = !!p.roundTrip;
  const pickupLocation = s(p.pickupResolved || p.pickupLocation);
  const destination = s(
    p.finalResolved || p.finalDestination || p.destination || ""
  );
  const rawDestinations = (
    Array.isArray(p.stopsResolved) && p.stopsResolved.length
      ? p.stopsResolved
      : Array.isArray(p.destinations)
      ? p.destinations
      : []
  );
  const normalizedStops = rawDestinations
    .map((stop, idx) => {
      const resolved = s(stop);
      if (!resolved) return null;
      const coordinate = Array.isArray(p.stopCoordinates)
        ? String(p.stopCoordinates[idx] || "").trim()
        : "";
      return {
        resolved,
        coordinate: isMeaningfulCoordinateString(coordinate) ? coordinate : "",
      };
    })
    .filter(Boolean);

  let destinations = normalizedStops.map((item) => item.resolved);
  let stopCoordinates = normalizedStops.map((item) => item.coordinate);

  if (
    roundTrip &&
    destination &&
    destination.toLowerCase() !== String(pickupLocation || "").toLowerCase() &&
    !destinations.some((stop) => stop.toLowerCase() === destination.toLowerCase())
  ) {
    destinations = [...destinations, destination];
    stopCoordinates = [...stopCoordinates, ""];
  }

  const body = {
    pickupLocation: pickupLocation || null,
    destination: destination || null,
    destinations,
    stopCoordinates,
    pickupLat: isFiniteLatLng(p.pickupLat, p.pickupLng) ? Number(p.pickupLat) : null,
    pickupLng: isFiniteLatLng(p.pickupLat, p.pickupLng) ? Number(p.pickupLng) : null,
    dropLat: isFiniteLatLng(p.dropLat, p.dropLng) ? Number(p.dropLat) : null,
    dropLng: isFiniteLatLng(p.dropLat, p.dropLng) ? Number(p.dropLng) : null,
    roundTrip,
    pickupDate: p.pickupDate || null,
    pickupTime: p.pickupTime24 || p.pickupTime || null,
  };

  if (!body.pickupLocation) return null;
  if (!body.roundTrip && !body.destination) return null;
  const cacheKey = JSON.stringify(body);
  const now = Date.now();
  const cached = tripEstimateResultCache.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    return cached.value;
  }

  const existing = tripEstimateInflight.get(cacheKey);
  if (existing) {
    return existing;
  }

  const traceId = `web-trip-${now}-${Math.random().toString(36).slice(2, 8)}`;
  const request = API.post("/api/vehicles/trip-estimate", body, {
    headers: {
      "X-Search-Trace-Id": traceId,
      "X-Trace-Id": traceId,
    },
  })
    .then(({ data }) => {
      const normalized = normalizeTripEstimateResponse(data);
      tripEstimateResultCache.set(cacheKey, {
        value: normalized,
        expiresAt: Date.now() + TRIP_ESTIMATE_CACHE_TTL_MS,
      });
      return normalized;
    })
    .finally(() => {
      tripEstimateInflight.delete(cacheKey);
    });

  tripEstimateInflight.set(cacheKey, request);
  return request;
}

/* ---------- Search vehicles (public) ---------- */
export async function searchVehicles(payload) {
  const p = payload || {};
  const debugSnapshot = buildScheduleSearchDebugSnapshot(p);
  const { normalizedPayload, queryParams } = debugSnapshot;
  const {
    pickupLocation,
    pickupDate,
    pickupTime,
    dropDate,
    dropTime,
    travelDistance,
    durationHours,
    category,
    types,
  } = queryParams;

  const hasCore =
    !!pickupLocation &&
    !!pickupDate &&
    !!pickupTime &&
    !!dropDate &&
    !!dropTime &&
    Number.isFinite(travelDistance) &&
    travelDistance > 0 &&
    Number.isFinite(durationHours) &&
    durationHours > 0 &&
    !!category;

  if (!hasCore) {
    console.warn("[WEB][searchVehicles] skipped before API call", {
      pickupLocation,
      pickupDate,
      pickupTime,
      dropDate,
      dropTime,
      travelDistance,
      durationHours,
      category,
      categoryList: normalizeCategoryList(
        Array.isArray(p.categories) ? p.categories : p.categories || p.category
      ),
      types,
      rawPayload: p,
      normalizedPayload,
    });
    return [];
  }

  const params = {
    ...queryParams,
  };

  const query = debugSnapshot.queryString;

  const traceId = `web-veh-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  let data;
  console.log("[WEB][searchVehicles] request", {
    traceId,
    endpoint: "/api/vehicles/grouped-within-radius",
    params,
    rawPayload: p,
    normalizedPayload,
  });
  try {
    const res = await API.get(`/api/vehicles/grouped-within-radius?${query}`, {
      headers: {
        "X-Search-Trace-Id": traceId,
        "X-Trace-Id": traceId,
      },
    });
    data = res?.data;
  } catch (e) {
    console.warn("[WEB][searchVehicles] API error", {
      traceId,
      status: e?.response?.status ?? null,
      message: e?.message ?? "Unknown error",
      responseData: e?.response?.data ?? null,
    });
    if (e?.response?.status === 400) return [];
    throw e;
  }

  const rawRows = Array.isArray(data)
    ? data
    : Array.isArray(data?.rows)
    ? data.rows
    : Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.vehicles)
    ? data.vehicles
    : Array.isArray(data?.content)
    ? data.content
    : [];

  console.log("[WEB][searchVehicles] response", {
    traceId,
    rowsCount: rawRows.length,
    responseShape: Array.isArray(data)
      ? "array"
      : data?.rows
      ? "rows"
      : data?.data
      ? "data"
      : data?.vehicles
      ? "vehicles"
      : data?.content
      ? "content"
      : typeof data,
    sample: rawRows[0] ?? null,
  });

  return rawRows.map((v, i) => {
    const ratingAverage =
      v?.ratingAverage != null ? Number(v.ratingAverage) : null;
    const ratingCount = v?.ratingCount != null ? Number(v.ratingCount) : 0;

    const eligibleDriverCount =
      v?.eligibleDriverCount != null ? Number(v.eligibleDriverCount) : 0;

    const eligibleDrivers = Array.isArray(v?.eligibleDrivers)
      ? v.eligibleDrivers
          .map((d) => ({
            id: d?.id ?? null,
            name: d?.name ?? "",
            phoneNumber: d?.phoneNumber ?? "",
          }))
          .filter((d) => d.id != null || d.phoneNumber)
      : [];

    return {
      id:
        v?.representativeRegistrationNumber ||
        v?.registrationNumber ||
        v?.id ||
        `row-${i}`,
      registrationNumber:
        v?.representativeRegistrationNumber || v?.registrationNumber,
      representativeRegistrationNumber:
        v?.representativeRegistrationNumber || v?.registrationNumber,
      availableCount:
        v?.availableCount != null ? Number(v.availableCount) : 1,
      brand: v?.brand ?? "",
      model: v?.model ?? "",
      type: v?.type ?? "",
      seatingCapacity:
        typeof v?.seatingCapacity === "number" ? v.seatingCapacity : null,
      fuelType: v?.fuelType ?? "",
      color: v?.color ?? "",
      yearOfPurchase: v?.yearOfPurchase ?? null,
      fare: Number(v?.fare ?? 0),
      baseFare:
        v?.baseFare != null ? Number(v.baseFare) : Number(v?.fare ?? 0),
      basePrice: v?.basePrice != null ? Number(v.basePrice) : undefined,
      fuelCost: v?.fuelCost != null ? Number(v.fuelCost) : undefined,
      toll: v?.toll != null ? Number(v.toll) : undefined,
      displayToll:
        v?.displayToll != null
          ? Number(v.displayToll)
          : v?.display_toll != null
          ? Number(v.display_toll)
          : v?.toll != null
          ? Number(v.toll)
          : undefined,
      commission: v?.commission != null ? Number(v.commission) : undefined,
      apiCharges: v?.apiCharges != null ? Number(v.apiCharges) : undefined,
      gst: v?.gst != null ? Number(v.gst) : undefined,
      ratingAverage,
      ratingCount,
      complianceVerified:
        v?.complianceVerified != null ? Boolean(v.complianceVerified) : null,
      complianceComplete:
        v?.complianceComplete != null ? Boolean(v.complianceComplete) : null,
      eligibleDriverCount,
      eligibleDrivers,
    };
  });
}

export default API;
